import React, { useState } from "react";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Autocomplete, Paper, TableCell, TableRow } from "@mui/material";
import TextField from "@mui/material/TextField";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { NumberInput } from "components/forms";
import dayjs from "dayjs";
import { useModals } from "hooks";
import { useDateFormatSelect } from "store";
import { CurrencyType, IInvestmentDetailHistoryValue, IInvestmentDetailValue } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../../constants";

interface IProps {
  item: IInvestmentDetailHistoryValue;
  onUpdate: (updatedItem: IInvestmentDetailValue) => void;
  onDelete: (id: number) => void;
}

export const HistoryDataRow: React.FC<IProps> = ({ item, onUpdate, onDelete }) => {
  const { deleteModal } = useModals();
  const dateFormat = useDateFormatSelect();

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [value, setValue] = useState<string>(item.value);
  const [currency, setCurrency] = useState<CurrencyType | null>(item.currency);
  const [date, setDate] = useState<string>(item.date);

  const updateItem = (updates: Partial<IInvestmentDetailValue>) => {
    const updatedItem = { ...item, ...updates };
    onUpdate(updatedItem);
  };

  const isValidDate = (dateString: string) => {
    const d = dayjs(dateString);
    return d.isValid();
  };

  const formattedDate = isValidDate(date) ? dayjs(date).format(dateFormat) : "";

  return (
    <TableRow>
      <TableCell>
        <NumberInput
          sx={{
            ":hover": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: "inherit",
                borderStyle: "solid",
                borderWidth: "1px"
              }
            },
            "& .MuiOutlinedInput-input": {
              textAlign: "left",
              height: "26px",
              width: "100px",
              padding: "0 5px",
              fontSize: "15px"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          }}
          numberInputProps={{
            thousandSeparator: true,
            allowNegative: false
          }}
          variant="outlined"
          value={value}
          onChange={(e) => {
            if (!e) return;
            const newValue = e.target.value;
            setValue(newValue);
            updateItem({ value: newValue });
          }}
        />
      </TableCell>
      {currency && (
        <TableCell>
          <Autocomplete
            options={currenciesArray}
            defaultValue={currency}
            onChange={(_, option) => {
              if (!option) return;
              setCurrency(option);
              updateItem({ currency: option });
            }}
            disableClearable
            PaperComponent={({ children }) => (
              <Paper style={{ display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden" }}>
                {children}
              </Paper>
            )}
            renderInput={(params) => (
              <TextField
                sx={{
                  width: "62px",
                  height: "20px",
                  padding: 0,
                  ":hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "inherit",
                      borderStyle: "solid",
                      borderWidth: "1px"
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      display: "block",
                      right: "0!important"
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#5d7782"
                    }
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiSvgIcon-root": {
                    color: "#5d7782"
                  },

                  "& .MuiOutlinedInput-root": {
                    fontSize: "15px",
                    padding: "0 !important"
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    right: "0 !important"
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff"
                  },

                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff"
                  },
                  "& .MuiAutocomplete-popupIndicator:hover": {
                    backgroundColor: "transparent"
                  }
                }}
                {...params}
              />
            )}
          />
        </TableCell>
      )}
      <TableCell>
        <DesktopDatePicker
          open={openDatePicker}
          views={["year", "month", "day"]}
          onClose={() => setOpenDatePicker(false)}
          format={dateFormat}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                minWidth: "335px",
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              InputProps: {
                readOnly: true,
                value: formattedDate
              },
              error: false,
              onClick: () => setOpenDatePicker(true),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none"
                },
                "& .MuiOutlinedInput-root": { padding: "0" },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                  border: "none",
                  padding: 0,
                  fontSize: "16px",
                  fontWeight: 500,
                  width: "103px",
                  height: "26px",
                  textAlign: "center",
                  color: "#0a3142",
                  marginRight: "5px"
                },
                "& .MuiIconButton-root": { padding: 0, display: "none" },
                "& .MuiSvgIcon-root": { width: "15px", display: "none" },
                "& .MuiInputAdornment-root": { width: "0px", display: "none" }
              }
            }
          }}
          value={isValidDate(date) ? dayjs(date) : null}
          onChange={(newDate) => {
            if (!newDate || !newDate.isValid()) return;
            const formattedDate = toIsoMidday(newDate.toISOString());
            setDate(formattedDate);
            updateItem({ date: formattedDate });
          }}
        />
      </TableCell>
      <TableCell>
        <CloseOutlinedIcon
          sx={{ fontSize: "16px", cursor: "pointer" }}
          onClick={() =>
            deleteModal({
              action: async () => {
                onDelete(item.id);
              },
              element: "Delete",
              message: "Are you sure want to delete data?",
              onPressSuccess: "Delete"
            })
          }
        />
      </TableCell>
    </TableRow>
  );
};
